<script setup lang="ts">
import { LogOut, Search, UserRound } from '@lucide/vue'
import { AUTH_SCREEN_COPY, authFooterParts } from '@zerohuman/shared'
import {
    Avatar,
    AvatarFallback,
    Button,
    Card,
    Separator,
    SidebarInset,
    SidebarProvider,
    SidebarTrigger,
    ZhAuthLayout,
    ZhBackdrop,
    ZhBanner,
    ZhGlitchText,
    ZhPageTitle,
    ZhPanel,
    ZhStatusDot,
    ZhStatusPill,
    ZhSystemLabel,
    ZhTooltip,
} from '@zerohuman/ui'
import logo from '@zerohuman/ui/mark.svg'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router'
import { api, type Me } from '@/api'
import AiAccountPrompt from '@/components/AiAccountPrompt.vue'
import AppSidebar from '@/components/AppSidebar.vue'
import CommandPalette from '@/components/CommandPalette.vue'
import EnterpriseAvatar from '@/components/EnterpriseAvatar.vue'
import SignInPanel from '@/components/SignInPanel.vue'
import { usePaymentBanner } from '@/composables/usePaymentBanner'
import { usePlanLimitBanner } from '@/composables/usePlanLimitBanner'
import { loadUiMode } from '@/composables/useUiMode'
import { aiAccountStatus } from '@/lib/aiAccountGuard'
import { holdsSeveralEnterprises, mustPickEnterprise } from '@/lib/enterprisePicker'
import { ACCOUNT_STEP_PATH } from '@/lib/onboardingGate'
import { loadOnboardingGate, recheckOnboardingGate } from '@/lib/onboardingGateLoad'
import {
    forgetPortalDestination,
    rememberPortalDestination,
    rememberSwitchDestination,
    takePortalDestination,
} from '@/lib/portalDestination'
import { isPublicScreen } from '@/lib/publicScreens'
import { setSessionEnterprise, setSessionGrant } from '@/lib/sessionEnterprise'
import { onSessionRead } from '@/lib/sessionRead'
import { closedEnterpriseNotice, signInErrorMessage } from '@/lib/signInError'
import { sendSignUpEvent } from '@/lib/signUpEvent'
import { setNavGrant } from '@/router'
import { setSentryUser } from '@/sentry'
import EnterprisePickerView from '@/views/EnterprisePickerView.vue'

const me = ref<Me | null>(null)
const palette = ref<InstanceType<typeof CommandPalette> | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)
/**
 * #1627: an owner who closed one enterprise but holds another open one comes back signed in to that one, with
 * `?error=enterprise_closed` still on the address. The sign-in screen's message never shows for them, so the shell
 * says the close happened, until they dismiss it.
 */
const closedNotice = ref(false)
const router = useRouter()
const route = useRoute()

/**
 * The picker stands in for the shell: before a person with several enterprises has chosen one for this
 * session, and whenever they come back to it to switch. It is not a page inside the shell, because the
 * sidebar and header describe an enterprise, and here that is the open question.
 */
const showPicker = computed(() => mustPickEnterprise(me.value) || route.path === '/enterprises')
/**
 * #1778: a screen that needs no sign-in (a Needs you email's decide link) is drawn on its own, whoever is reading:
 * not the sign-in screen, which would stand between a signed-out owner and the link they followed, and not the
 * signed-in shell, whose header and sidebar describe an enterprise the page does not need.
 */
const publicScreen = computed(() => isPublicScreen(route.path))
/**
 * A page that fills the screen (chat) scrolls inside itself, never the document: the shell is held to the
 * viewport and the page takes whatever height the header leaves, however tall the header wraps to.
 */
const fillsViewport = computed(() => route.meta.fillsViewport === true)
const severalEnterprises = computed(() => holdsSeveralEnterprises(me.value))
/** The sign-in footer's sentence, with Terms and Privacy Policy as links (#1933). */
const footerParts = authFooterParts()
/** Same button, two jobs: with one enterprise it leaves; with several it goes to where leaving is one option. */
const leaveLabel = computed(() => (severalEnterprises.value ? 'Switch enterprise or log out' : 'Log out'))

/**
 * What the version pill cannot say on its own: the release bump is a commit *after* the merge it names, so
 * a just-merged PR is live under the previous version number. The commit is the unambiguous answer.
 */
const buildTitle = computed(() => {
    const version = `Live production build: ${me.value?.version ?? ''}`
    const commit = me.value?.commit
    return commit ? `${version} (commit ${commit.slice(0, 8)})` : version
})

/**
 * The plan badge's hint. The badge itself is one word, which is the point — a header says where you are,
 * not what you are entitled to — so the tagline, who the plan is for as /pricing names it, carries the rest.
 */
const planTitle = computed(() => {
    const tier = me.value?.tier
    return tier ? `${tier.name} plan — ${tier.tagline}` : null
})

/**
 * #1796: the slim banner back to Step 1, on every page, while the owner's enterprise has no AI account. Only on a
 * status that reads `not_connected`: an unknown status tells nobody they have no account on a guess. Not on the step
 * itself, and never for somebody who is not the owner (the same audience the landing has).
 */
const showAiAccountBanner = computed(
    () => me.value?.isOwner === true && aiAccountStatus() === 'not_connected' && route.path !== ACCOUNT_STEP_PATH,
)

/**
 * #2122: the plan-limit banner, on every signed-in page: near a limit it warns and can be dismissed, at a limit it
 * stays until the limit clears. Read when the shell mounts and again after each move, like the AI-account banner.
 */
const planLimit = usePlanLimitBanner(me)
const planLimitBanner = planLimit.banner

/**
 * The past-due banner, on every signed-in page while the plan's last payment has not gone through: the plan ends if
 * it cannot be collected, so it is said everywhere, not only on Billing. Read with the plan allowances.
 */
const payment = usePaymentBanner(me)
const paymentBanner = payment.banner

/**
 * #394: while Zero Human staff have the enterprise paused, every member is told why nothing runs, in fixed words: the
 * staff member's reason stays in their action log. Read with the session, and again after each move while it shows,
 * so it goes at the next move once staff unpause the enterprise.
 */
const enterprisePaused = ref(false)

async function recheckPaused(): Promise<void> {
    if (!enterprisePaused.value) {
        return
    }
    try {
        enterprisePaused.value = (await api.me())?.enterprisePaused === true
    } catch {
        // A failed read says nothing either way: the banner stays as it was until the next move.
    }
}

/**
 * #2217: a page that changed the session (the enterprise renamed on the Enterprise page) reads it again and hands the
 * fresh read here, so the header and the enterprise picker show the new name without a reload.
 */
const stopSessionRead = onSessionRead(fresh => {
    me.value = fresh
    enterprisePaused.value = fresh?.enterprisePaused === true
})
onBeforeUnmount(stopSessionRead)

onMounted(async () => {
    const params = new URLSearchParams(window.location.search)
    error.value = signInErrorMessage(params)
    closedNotice.value = params.get('error') === 'enterprise_closed'
    me.value = await api.me()
    // #633: set before any page renders, so a list page restores this enterprise's saved choices for its first load.
    setSessionEnterprise(me.value && !mustPickEnterprise(me.value) ? me.value.enterprise.id : null)
    enterprisePaused.value = me.value?.enterprisePaused === true
    // #1778: a decide link carries its token in the address, so it is never kept as where to go after signing in.
    if ((!me.value || mustPickEnterprise(me.value)) && !isPublicScreen(route.path))
        rememberPortalDestination(route.fullPath)
    setSentryUser(me.value?.user ?? null)
    // #622: what this person may reach, handed to the router so a screen their grant does not cover is not
    // opened by typing its address. The sidebar reads the same grant off `me` below and offers only the
    // destinations it covers, so the portal fits the person rather than refusing them at every click.
    const grant = {
        isOwner: me.value?.isOwner,
        scopes: me.value?.scopes,
        platformAdmin: me.value?.user.platformAdmin === true,
    }
    // PR #991: the same grant, kept beside the session's enterprise for a screen that links into one of its pages.
    setSessionGrant(grant)
    setNavGrant(grant)
    // #1949: every screen reads the mode saved on the person. Not awaited: carrying a browser's old choice over to
    // the account is one write the shell never waits on. Standard shows until then.
    void loadUiMode(me.value?.user.uiMode)
    // Not on the picker itself: a reload there still owes the switch its page, kept until an enterprise is chosen.
    if (me.value && !mustPickEnterprise(me.value) && route.path !== '/enterprises') {
        const destination = takePortalDestination()
        if (destination && route.path === '/' && router.resolve(destination).matched.length > 0)
            await router.replace(destination)
    }
    loading.value = false
    // #1796: not awaited. Every page opens without an AI account, so the shell never waits on the status read; the
    // banner follows when it answers, and a new owner's one-time landing on Step 1 moves the open screen then.
    void loadOnboardingGate(me.value)
    // A session whose sign-in founded its enterprise is a sign-up: told once to the deployment's tag manager, if any.
    sendSignUpEvent(me.value)
    // #2122: not awaited either: the banner follows when the allowances answer.
    void planLimit.refresh()
    void payment.refresh()
})

// #1796: the status is read again after each move while it is not connected, so the banner clears at the next move
// once an account is connected elsewhere. Nothing is held on it. #2122: the plan allowances are read again too, so a
// limit reached, or cleared, shows at the next move.
router.afterEach(() => {
    recheckOnboardingGate()
    void planLimit.refresh()
    void payment.refresh()
    void recheckPaused()
})

// Switching enterprise, by the header's button or any other way to the picker, opens the next enterprise on the same
// screen. Not on the first load: an address typed or followed straight to the picker came from no screen.
router.afterEach((to, from, failure) => {
    if (failure || to.path !== '/enterprises' || from.path === '/enterprises' || from.matched.length === 0) return
    rememberSwitchDestination(router, from.fullPath)
})

/** The header's search icon is the pointer-driven way in; the shortcuts it names are the other. */
function openCommandPalette(): void {
    void palette.value?.open()
}

async function logout(): Promise<void> {
    await api.logout()
    // The next person to sign in on this tab starts on the map, not where this one was switching from.
    forgetPortalDestination()
    me.value = null
    setSessionEnterprise(null)
    await router.push('/')
}

async function leave(): Promise<void> {
    if (severalEnterprises.value) {
        await router.push('/enterprises')
        return
    }
    await logout()
}
</script>

<template>
	<!-- #1778: the decide page a Needs you email links to, on its own, signed in or not. -->
	<RouterView v-if="publicScreen" />

	<div v-else-if="loading" class="flex min-h-svh items-center justify-center">
		<ZhSystemLabel tone="system">Booting…</ZhSystemLabel>
	</div>

	<!--
		#1933: the sign-in and sign-up screen, as John approved it on 7 Oct. A small logo, one title and its
		subtitle, the quiet sign-in panel and a footer; from `lg` up, a splash saying what Zero Human is for. Built
		only from @zerohuman/ui, with its words from @zerohuman/shared, which the API's invitation page reads too.
		The AI-account notice that used to sit beside the controls is on the AI account step now (#1717).
	-->
	<ZhAuthLayout v-else-if="!me">
		<template #form>
			<div class="flex flex-col gap-6">
				<div class="flex flex-col gap-2">
					<ZhPageTitle>{{ AUTH_SCREEN_COPY.title }}</ZhPageTitle>
					<p class="text-muted-foreground">{{ AUTH_SCREEN_COPY.subtitle }}</p>
				</div>
				<ZhStatusPill v-if="error" tone="failed">{{ error }}</ZhStatusPill>
				<!-- #636: Google, Microsoft, GitHub, or a code emailed to you. -->
				<SignInPanel />
			</div>
		</template>
		<template #footer>
			<p class="text-sm text-muted-foreground"><template v-for="part in footerParts" :key="part.text"><a v-if="part.href" :href="part.href" target="_blank" rel="noopener noreferrer" class="text-foreground underline underline-offset-4">{{ part.text }}</a><template v-else>{{ part.text }}</template></template></p>
		</template>
		<template #splash>
			<ZhPanel class="flex w-full">
				<div class="flex w-full flex-col justify-center gap-10 px-4 py-10 xl:px-10 xl:py-16">
					<!-- ZhGlitchText is font-agnostic by design; the display face is the caller's call. At rest: no glitch. -->
					<ZhGlitchText
						as="h2"
						:text="AUTH_SCREEN_COPY.splashHeadline"
						:glitch="false"
						class="font-display text-h-figure uppercase leading-tight tracking-display"
					/>
					<!--
						An illustration, not live data: hidden from assistive tech, and inert, so its buttons take no
						focus and no click. The page's one real action stays "Continue with email".
					-->
					<div class="flex flex-col gap-4" aria-hidden="true" inert>
						<Card class="gap-3 px-5">
							<div class="flex items-center gap-3">
								<Avatar>
									<AvatarFallback class="font-mono text-xs">COO</AvatarFallback>
								</Avatar>
								<span class="text-sm font-medium">{{ AUTH_SCREEN_COPY.exampleWho }}</span>
								<ZhStatusPill tone="action" class="ml-auto">{{ AUTH_SCREEN_COPY.examplePill }}</ZhStatusPill>
							</div>
							<p class="text-sm">{{ AUTH_SCREEN_COPY.exampleText }}</p>
							<div class="flex flex-wrap gap-2">
								<Button type="button" variant="outline" size="sm" tabindex="-1">{{ AUTH_SCREEN_COPY.exampleApprove }}</Button>
								<Button type="button" variant="ghost" size="sm" tabindex="-1">{{ AUTH_SCREEN_COPY.exampleRequestChanges }}</Button>
							</div>
						</Card>
						<p class="ml-8 flex items-center gap-2 text-sm text-muted-foreground">
							<ZhStatusDot state="complete" :pulse="false" />{{ AUTH_SCREEN_COPY.activityDone }}
						</p>
						<p class="ml-16 flex items-center gap-2 text-sm text-muted-foreground">
							<ZhStatusDot state="active" :pulse="false" />{{ AUTH_SCREEN_COPY.activityDoing }}
						</p>
					</div>
				</div>
			</ZhPanel>
		</template>
	</ZhAuthLayout>

	<EnterprisePickerView v-else-if="showPicker" :me="me" @logout="logout" />

	<SidebarProvider v-else>
		<AppSidebar
			:is-owner="me.isOwner"
			:scopes="me.scopes"
			:platform-admin="me.user.platformAdmin === true"
			:mail-address="me.mailAddress"
		/>
		<SidebarInset :class="fillsViewport ? 'h-dvh min-w-0 overflow-hidden' : 'min-w-0'">
			<header class="relative flex min-h-12 shrink-0 flex-wrap items-center gap-3 px-4 py-2">
				<ZhBackdrop intensity="chrome" />
				<!--
					Mobile only, and dressed as the other two header controls: an outline button with an icon
					and a hint. It was a bare ghost icon, which read as chrome rather than as the control that
					opens the whole nav on the screen size where the nav is hidden.
				-->
				<ZhTooltip text="Open the menu">
					<SidebarTrigger class="relative z-10 md:hidden" variant="outline" size="sm" />
				</ZhTooltip>
				<RouterLink to="/" class="relative z-10 flex items-center gap-2">
					<img :src="logo" alt="Zero Human" class="size-7" />
					<ZhSystemLabel as="span" class="hidden sm:inline">Zero Human</ZhSystemLabel>
				</RouterLink>
				<ZhTooltip v-if="me.preview" text="A per-PR preview stack: throwaway data, and runs do not execute here.">
					<ZhStatusPill tone="neutral" class="relative z-10">Preview {{ me.preview }}</ZhStatusPill>
				</ZhTooltip>
				<!--
					Production, in the system channel: which build is actually serving you. Grey says throwaway
					preview, cyan says live, so the two environments never read the same at a glance. Neither takes
					one of the BG §18.4 status colours: a build is not a run. No "v" — the pill's ◆ already says this
					is a build number, and the prefix was the only thing on it that was decoration.
				-->
				<!--
					#1532: hidden below sm, like the wordmark and enterprise name, so a phone header stays on one
					row. The gate is on this container, not the pill: the pill sets its own display from a scoped,
					unlayered rule that a utility class passed to it would lose to. The stacking above the
					backdrop moves here with it. The preview pill above is left ungated on purpose.
				-->
				<div v-else-if="me.version" class="relative z-10 hidden sm:inline-flex">
					<ZhTooltip :text="buildTitle">
						<ZhStatusPill tone="system">{{ me.version }}</ZhStatusPill>
					</ZhTooltip>
				</div>
				<!--
					Where you are. The plan replaced the CEO's own name here: there is exactly one human in a
					Zero Human enterprise and they are the one reading the screen, so their name was the least
					informative string in the chrome. Which enterprise this is, and which plan it is on, are not —
					a CEO may hold several, and the leave button beside them is how they move between them.
				-->
				<div class="relative z-10 ml-auto flex items-center gap-3">
					<EnterpriseAvatar :enterprise="me.enterprise" class="hidden size-6 sm:inline-flex" fallback-class="text-xs" />
					<ZhSystemLabel as="span" tone="muted" class="hidden sm:inline">{{ me.enterprise.name }}</ZhSystemLabel>
					<ZhTooltip v-if="me.tier" :text="planTitle">
						<ZhStatusPill tone="plan">{{ me.tier.name }}</ZhStatusPill>
					</ZhTooltip>
					<!--
						#630: what this person holds here and who invited them. In the header rather than the nav,
						beside the other controls about who you are, and open to every grant however narrow.
					-->
					<ZhTooltip text="Your account — what you hold here">
						<Button as-child size="sm" variant="outline">
							<RouterLink to="/account" aria-label="Your account" data-testid="account-link">
								<UserRound />
							</RouterLink>
						</Button>
					</ZhTooltip>
					<!-- data-command-palette-trigger: the palette ignores this click, which would otherwise read as "outside". -->
					<ZhTooltip text="Search — ⌘K / Ctrl+K, or just G">
						<Button
							type="button"
							size="sm"
							variant="outline"
							data-command-palette-trigger
							aria-label="Search"
							@click="openCommandPalette"
						>
							<Search />
						</Button>
					</ZhTooltip>
					<ZhTooltip :text="leaveLabel">
						<Button type="button" size="sm" variant="outline" :aria-label="leaveLabel" @click="leave">
							<LogOut />
						</Button>
					</ZhTooltip>
				</div>
			</header>
			<!-- The header's rule: the kit's hairline rather than a border the shell draws itself. -->
			<Separator />
			<!-- #1627: the enterprise this owner just closed is closed; they are still in their other, open one. -->
			<ZhBanner
				v-if="closedNotice"
				data-testid="enterprise-closed-banner"
				tone="system"
				dismissible
				:full-text="closedEnterpriseNotice(me.enterprise.name)"
				@dismiss="closedNotice = false"
			>
				{{ closedEnterpriseNotice(me.enterprise.name) }}
			</ZhBanner>
			<!-- #394: Zero Human staff paused this enterprise. Fixed words for every member, never the staff reason. -->
			<ZhBanner v-if="enterprisePaused" data-testid="enterprise-paused-banner" tone="system">
				Zero Human has paused this enterprise. Nothing runs until it is unpaused.
			</ZhBanner>
			<!-- #1796: browsing needs no AI account; this one line says what does, and links back to Step 1. -->
			<ZhBanner v-if="showAiAccountBanner" data-testid="ai-account-banner">
				No AI account is connected yet: look around freely, but nothing can run until one is.
				<template #action>
					<RouterLink :to="ACCOUNT_STEP_PATH">Connect your AI account</RouterLink>
				</template>
			</ZhBanner>
			<!--
				A payment that did not go through: said on every page until it is collected or the plan ends, in the
				tone of work that is stopped, and never dismissible. The button only with access to Billing.
			-->
			<ZhBanner
				v-if="paymentBanner"
				data-testid="payment-banner"
				tone="blocked"
				:full-text="paymentBanner.notice.sentence"
			>
				{{ paymentBanner.notice.sentence }}
				<template v-if="paymentBanner.showsButton" #action>
					<Button as-child size="xs" variant="link">
						<RouterLink :to="paymentBanner.notice.buttonPath">{{ paymentBanner.notice.buttonLabel }}</RouterLink>
					</Button>
				</template>
			</ZhBanner>
			<!--
				#2122: the one plan-limit banner, its words the shared sentence; the button only with access to Billing.
				The sentence is long, so `full-text` keeps all of it readable when the line cuts it short: the kit
				tooltip on hover and keyboard focus, and a tap shows it in full.
			-->
			<ZhBanner
				v-if="planLimitBanner"
				data-testid="plan-limit-banner"
				:tone="planLimitBanner.tone"
				:dismissible="planLimitBanner.dismissible"
				:full-text="planLimitBanner.notice.sentence"
				@dismiss="planLimit.dismiss()"
			>
				{{ planLimitBanner.notice.sentence }}
				<template v-if="planLimitBanner.showsButton" #action>
					<Button as-child size="xs" variant="link">
						<RouterLink :to="planLimitBanner.notice.buttonPath">{{ planLimitBanner.notice.buttonLabel }}</RouterLink>
					</Button>
				</template>
			</ZhBanner>
			<RouterView />
		</SidebarInset>
		<!-- Mounted once, inside the signed-in shell: Cmd/Ctrl+K or "g" jumps from any authenticated page. -->
		<CommandPalette ref="palette" />
		<!-- #1796: where an action blocked for want of an AI account says so, with the way to the connect card. -->
		<AiAccountPrompt />
	</SidebarProvider>
</template>
